import { useCallback, useRef, useState } from 'react';

import { StatusBannerAutoHide } from '@xing-com/banner';
import {
  isValidFileSize,
  ACCEPTED_FILES,
  MIN_IMAGE_WIDTH,
  createImage,
  isValidImageRatio,
} from '@xing-com/crate-articles-image-cropper-modal';
import { IconImage } from '@xing-com/icons';

import * as Styled from '../styles';
import { type ImageToolProps } from '../types';

export const ImageTool: React.FC<ImageToolProps> = ({
  editor,
  disabled,
  onImageUpload,
}) => {
  const [error, setError] = useState<string | undefined>();
  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!editor) {
        return;
      }

      const file = e.target.files?.item(0);
      if (!file) {
        setError(`Error selecting the file.`);
        return;
      }

      if (!isValidFileSize(file)) {
        setError(`File size exceeds the maximum limit of 10MB.`);
        return;
      }

      try {
        const image = await createImage(URL.createObjectURL(file));

        const isWideEnough = image.width >= MIN_IMAGE_WIDTH;
        const isRatioOk = isValidImageRatio(image, 0.05);

        if (!isWideEnough || !isRatioOk) {
          setError(
            `Image must be at least ${MIN_IMAGE_WIDTH}px wide and have a 16:9 aspect ratio.`
          );
          return;
        }

        const { downloadUrl, error } = await onImageUpload(file);

        if (error || !downloadUrl) {
          setError('Error uploading the image.');
          return;
        }

        editor
          .chain()
          .focus()
          .addXingImage(downloadUrl)
          .createParagraphNear()
          .run();
      } catch (error) {
        console.log({ error });
        setError('Error uploading the image.');
        return;
      }

      e.target.value = '';
      return;
    },
    [editor, onImageUpload]
  );

  return (
    <div>
      <Styled.ToolBarButton
        onClick={() => {
          inputFileRef.current?.click?.();
        }}
        disabled={disabled}
      >
        <IconImage width={24} height={24} />
      </Styled.ToolBarButton>
      <input
        ref={inputFileRef}
        data-qa="articles-image-tool-input"
        data-testid="articles-image-tool-input"
        type="file"
        accept={ACCEPTED_FILES}
        onChange={handleImageUpload}
        hidden
      />
      <StatusBannerAutoHide
        handleOnClose={() => setError(undefined)}
        display="fixed"
        show={!!error}
        variant="error"
      >
        {error}
      </StatusBannerAutoHide>
    </div>
  );
};
